<template>
  <CashierLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
            <h1 class="h4 mb-0">Fin de journée</h1>
            <span
              class="badge"
              :class="statusClass"
              :aria-label="`Statut de la journée : ${statusLabel}`"
              data-tour="cashier-end-day-status"
            >
              {{ statusLabel }}
            </span>
          </div>
          <small class="text-muted">Résumé des ventes du jour, billetage et clôture du service.</small>
        </div>
        <div class="d-flex align-items-center gap-2">
          <input
            v-model="selectedDate"
            type="date"
            class="form-control form-control-sm end-day-date"
            :max="todayDate"
            @change="fetchSummary"
          />
          <button type="button" class="btn btn-outline-secondary btn-sm btn-action-icon" title="Actualiser" @click="fetchSummary">
            <i class="fa fa-rotate"></i>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2">{{ successMessage }}</div>
      <div v-if="drawerWarningMessage" class="alert alert-warning py-2">{{ drawerWarningMessage }}</div>
      <div v-if="isSelectedDateInFuture" class="alert alert-warning py-2">
        Impossible de clôturer une journée qui n'est pas encore arrivée.
      </div>

      <div v-if="isLoading" class="text-center py-5">
        <i class="fa fa-spinner fa-spin me-1"></i>
        Chargement...
      </div>

      <div v-else>
        <div class="row g-3 mb-3" data-tour="cashier-end-day-overview">
          <div class="col-12 col-md-6 col-xl-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Caisse active</div>
                <div class="h5 mb-0">{{ activeCashRegisterName || '-' }}</div>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Ouverte le</div>
                <div class="h6 mb-0">{{ activeOpenedAt ? $format.datetime(activeOpenedAt, shopTimezone) : '-' }}</div>
              </div>
            </div>
          </div>
          <div v-if="cashRegisterAutoCloseEnabled" class="col-12 col-md-6 col-xl-3">
            <div class="card border-0 shadow-sm h-100 end-day-closing-card">
              <div class="card-body">
                <div class="small text-muted mb-1">Clôture prévue</div>
                <div class="h6 mb-1">{{ $format.date(maxClosingDateTimeValue) }}</div>
                <div class="closing-time">
                  <i class="fa fa-clock me-1"></i>
                  {{ formatTimeWithSeconds(maxClosingDateTimeValue) }}
                </div>
                <small class="text-muted d-block mt-1">Heure maximum automatique</small>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <div class="card border-0 shadow-sm h-100 end-day-real-closing-card">
              <div class="card-body">
                <div class="small text-muted mb-1">Clôture réelle</div>
                <template v-if="actualClosingDateTimeValue">
                  <div class="h6 mb-1">{{ $format.date(actualClosingDateTimeValue, shopTimezone) }}</div>
                  <div class="real-closing-time">
                    <i class="fa fa-check-circle me-1"></i>
                    {{ formatTimeWithSeconds(actualClosingDateTimeValue, true) }}
                  </div>
                </template>
                <template v-else>
                  <div class="h6 mb-1">-</div>
                  <small class="text-muted">En attente du billetage</small>
                </template>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Ventes validées</div>
                <div class="h4 mb-0">{{ summary.sales_count || 0 }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-3 mb-3" data-tour="cashier-end-day-cash-totals">
          <div class="col-12 col-md-6" :class="cashTotalColumnClass">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Fond initial</div>
                <div class="h4 mb-0">{{ $format.currency(summary.opening_amount || 0) }}</div>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6" :class="cashTotalColumnClass">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Espèces encaissées</div>
                <div class="h4 mb-0">{{ $format.currency(summary.cash_sales_amount || 0) }}</div>
              </div>
            </div>
          </div>
          <div v-if="isWalletEnabled" class="col-12 col-md-6" :class="cashTotalColumnClass">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Recharge</div>
                <div class="h4 mb-0">{{ $format.currency(summary.wallet_totals?.recharges || 0) }}</div>
                <small class="text-muted">Opérations validées</small>
              </div>
            </div>
          </div>
          <div v-if="isWalletEnabled" class="col-12 col-md-6" :class="cashTotalColumnClass">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Dépôts</div>
                <div class="h4 mb-0">{{ $format.currency(summary.wallet_totals?.deposits || 0) }}</div>
                <small class="text-muted">Opérations validées</small>
              </div>
            </div>
          </div>
          <div v-if="isWalletEnabled" class="col-12 col-md-6" :class="cashTotalColumnClass">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Retraits</div>
                <div class="h4 mb-0">{{ $format.currency(summary.wallet_totals?.withdrawals || 0) }}</div>
                <small class="text-muted">Opérations validées</small>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6" :class="cashTotalColumnClass">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Montant théorique</div>
                <div class="h4 mb-0">{{ $format.currency(summary.expected_amount || 0) }}</div>
                <small class="text-muted d-block mt-1">Fond initial + flux d’espèces nets</small>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-3 mb-3" data-tour="cashier-end-day-other-totals">
          <div class="col-12 col-md-6 col-xl-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Ventes à crédit</div>
                <div class="h4 mb-0">{{ $format.currency(summary.credit_sales_amount || 0) }}</div>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Ventes annulées</div>
                <div class="h4 mb-0">{{ summary.cancelled_sales_count || 0 }}</div>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Carte</div>
                <div class="h4 mb-0">{{ $format.currency(summary.card_sales_amount || 0) }}</div>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6 col-xl-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body">
                <div class="small text-muted mb-1">Mobile Money</div>
                <div class="h4 mb-0">{{ $format.currency(summary.mobile_money_sales_amount || 0) }}</div>
              </div>
            </div>
          </div>
        </div>

        <section v-if="hasSalesForDay" class="end-day-section mb-3">
          <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
            <div>
              <h2 class="h5 mb-1">Produits vendus</h2>
              <p class="text-muted mb-0">
                Produits vendus dans la caisse de l'utilisateur connecté.
              </p>
            </div>
          </div>

          <BootstrapDataTable
            :rows="soldProducts"
            :columns="soldProductColumns"
            row-key="_row_key"
            :sort-by="soldProductsTable.sortBy"
            :sort-dir="soldProductsTable.sortDir"
            :per-page="soldProductsTable.perPage"
            searchable
            v-model:search-value="soldProductsSearch"
            search-placeholder="Rechercher un produit vendu..."
            export-file-name="produits-vendus-fin-journee.xlsx"
            export-sheet-name="Produits vendus"
            print-title="Produits vendus"
            empty-text="Aucun produit vendu pour cette journée."
            @sort-change="handleSoldProductsSortChange"
            @refresh="fetchSummary"
          >
            <template #cell-quantity="{ value }">
              <span class="fw-semibold">{{ Number(value || 0) }}</span>
            </template>
            <template #cell-unit_price="{ value }">
              {{ $format.currency(value || 0) }}
            </template>
            <template #cell-total_amount="{ value }">
              <span class="fw-semibold">{{ $format.currency(value || 0) }}</span>
            </template>
          </BootstrapDataTable>
        </section>

        <section v-if="hasWalletTransactions" class="end-day-section mb-3">
          <h2 class="h5 mb-2">Transactions des portefeuilles</h2>
          <BootstrapDataTable :rows="walletTransactions" :columns="walletTransactionColumns" row-key="id" :per-page="10" searchable search-placeholder="Rechercher une transaction..." export-file-name="transactions-portefeuilles-fin-journee.xlsx" export-sheet-name="Portefeuilles" print-title="Transactions des portefeuilles" empty-text="Aucune transaction électronique.">
            <template #cell-created_at="{ value }">{{ $format.datetime(value, shopTimezone) }}</template>
            <template #cell-type="{ value }">{{ walletOperationLabel(value) }}</template>
            <template #cell-amount="{ value }"><span class="fw-semibold">{{ $format.currency(value || 0) }}</span></template>
            <template #cell-balance_after="{ value }">{{ $format.currency(value || 0) }}</template>
            <template #cell-status="{ value }">
              <span class="badge" :class="walletStatusBadgeClass(value)">
                {{ walletStatusLabel(value) }}
              </span>
            </template>
          </BootstrapDataTable>
        </section>

        <div v-if="hasSessionOrCashCount" class="card border-0 shadow-sm">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap">
              <div>
                <h2 class="h5 mb-2">Billetage du {{ $format.date(selectedDate) }}</h2>
                <p class="text-muted mb-0">
                  Caisse : <strong>{{ summary.cash_register?.name || summary.session?.cash_register?.name || '-' }}</strong>.
                  Le montant théorique reprend le fond initial et les ventes espèces de la session.
                </p>
                <div v-if="pendingSalesCount > 0 && !cashCount" class="alert alert-warning mt-3 mb-0 pending-sales-alert">
                  <div>{{ pendingSalesBlockingMessage }}</div>
                  <router-link :to="{ name: 'CashierSales' }" class="btn btn-warning btn-sm mt-2">
                    <i class="fa fa-arrow-left me-1"></i>
                    Voir les ventes en attente
                  </router-link>
                </div>
              </div>
              <div class="d-flex gap-2">
                <button
                  v-if="!cashCount"
                  type="button"
                  class="btn btn-primary btn-lg btn-icon-label btn-close-day-heartbeat"
                  :disabled="isSelectedDateInFuture || pendingSalesCount > 0"
                  :title="closeDayButtonTitle"
                  @click="openCreateDialog"
                >
                  <i class="fa fa-cash-register"></i>
                  <span class="btn-label ms-1">Clôturer ma journée</span>
                </button>
              </div>
            </div>

            <div v-if="cashCount" class="row g-3 mt-2">
              <div class="col-md-3">
                <label class="form-label small text-muted mb-1">Montant théorique</label>
                <div class="fw-semibold">{{ $format.currency(cashCount.expected_amount || 0) }}</div>
              </div>
              <div class="col-md-3">
                <label class="form-label small text-muted mb-1">Montant compté</label>
                <div class="fw-semibold">{{ $format.currency(cashCount.counted_amount || 0) }}</div>
              </div>
              <div class="col-md-3">
                <label class="form-label small text-muted mb-1">Différence</label>
                <div
                  class="fw-semibold"
                  :class="{
                    'text-success': Number(cashCount.difference || 0) > 0,
                    'text-danger': Number(cashCount.difference || 0) < 0,
                  }"
                >
                  {{ $format.currency(cashCount.difference || 0) }}
                </div>
              </div>
              <div class="col-md-3">
                <label class="form-label small text-muted mb-1">Créé le</label>
                <div class="fw-semibold">{{ $format.datetime(cashCount.created_at, shopTimezone) }}</div>
              </div>
            </div>

            <div v-if="cashCountComment" class="alert alert-light border mt-3 mb-0 cash-count-comment">
              <div class="small text-muted fw-semibold mb-1">Commentaire</div>
              <div>{{ cashCountComment }}</div>
            </div>

            <div v-if="displayedCashCountLines.length" class="table-responsive mt-3">
              <table class="table table-sm table-striped table-hover table-bordered align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Libellé</th>
                    <th class="text-end">Valeur</th>
                    <th class="text-end">Quantité</th>
                    <th class="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="line in displayedCashCountLines" :key="line.id || line.label">
                    <td>{{ line.label }}</td>
                    <td class="text-end">{{ $format.currency(line.denomination || 0) }}</td>
                    <td class="text-end">{{ line.quantity }}</td>
                    <td class="text-end">{{ $format.currency(line.line_total || 0) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <PrimevueDialog
        v-model:visible="formDialogVisible"
        :modal="true"
        :closable="false"
        :style="{ width: '900px', maxWidth: '100%' }"
      >
        <template #header>
          <h5 class="mb-0">{{ cashCountDialogTitle }}</h5>
        </template>
        <CashCountForm
          v-if="formDialogVisible"
          mode="create"
          :cash-count="null"
          :finalize-on-create="true"
          :cash-count-enabled="cashCountEnabled"
          :cash-register-name="activeCashRegisterName"
          :initial-opening-amount="activeOpeningAmount"
          :initial-expected-amount="Number(summary.expected_amount || 0)"
          :initial-operation-date="selectedDate"
          :initial-start-at="activeOpenedAt"
          @saved="handleCashCountSaved"
          @cancel="closeDialog"
        />
      </PrimevueDialog>
    </template>
  </CashierLayout>
</template>

<script>
import CashCountForm from '@/views/cash/CashCountForm.vue';
import {
  loadThermalPrinterSettings,
  openThermalDrawer,
} from '@/services/thermalPrinter';
import { showAppToast } from '@/support/toastBus';

function formatLocalDate(date) {
  const pad = (number) => String(number).padStart(2, '0');

  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join('-');
}

export default {
  name: 'CashierEndOfDayView',
  components: {
    CashCountForm,
  },
  data() {
    return {
      selectedDate: formatLocalDate(new Date()),
      summary: {},
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      drawerWarningMessage: '',
      formDialogVisible: false,
      currentClock: new Date(),
      clockIntervalId: null,
      soldProductsSearch: '',
      stockAlertProductsSearch: '',
      soldProductsTable: {
        sortBy: 'name',
        sortDir: 'asc',
        perPage: 10,
      },
      stockAlertProductsTable: {
        sortBy: 'stock_quantity',
        sortDir: 'asc',
        perPage: 10,
      },
    };
  },
  computed: {
    cashCount() {
      return this.summary.cash_count || null;
    },
    cashCountComment() {
      return String(this.cashCount?.comment || '').trim();
    },
    shopInfo() {
      return this.$store.getters['shop/shopInfo'] || {};
    },
    shopTimezone() {
      return this.$store.getters['shop/timezone'] || this.shopInfo.timezone || undefined;
    },
    cashCountEnabled() {
      if (typeof this.summary.cash_count_enabled === 'boolean') {
        return this.summary.cash_count_enabled;
      }

      return this.shopInfo.cash_count_enabled !== false;
    },
    generalSettings() {
      return this.$store.getters['userSettings/general'] || {};
    },
    cashDrawerEnabled() {
      return this.generalSettings.cash_drawer_enabled === true;
    },
    cashRegisterAutoCloseEnabled() {
      return this.shopInfo.cash_register_auto_close_enabled === true;
    },
    cashCountDialogTitle() {
      return this.cashCountEnabled ? 'Billetage de fin de journée' : 'Clôture de ma journée';
    },
    statusLabel() {
      if (!this.cashCount) return 'À faire';
      return this.cashCount.status === 'validated' ? 'Clôturée' : 'Brouillon';
    },
    statusClass() {
      if (!this.cashCount) return 'bg-secondary';
      return this.cashCount.status === 'validated' ? 'bg-success' : 'bg-warning text-dark';
    },
    activeCashRegisterName() {
      return this.summary.cash_register?.name
        || this.summary.session?.cash_register?.name
        || this.cashCount?.cash_register?.name
        || '';
    },
    activeOpeningAmount() {
      if (this.summary.session || this.cashCount) {
        return Number(this.summary.opening_amount ?? this.cashCount?.opening_amount ?? 0);
      }

      return null;
    },
    activeOpenedAt() {
      return this.summary.session?.opened_at || this.cashCount?.start_at || '';
    },
    maxClosingDateTimeValue() {
      const date = this.datePartFromValue(this.activeOpenedAt) || this.selectedDate;

      return `${date}T23:59:59`;
    },
    actualClosingDateTimeValue() {
      return this.summary.session?.closed_at
        || this.cashCount?.cash_register_session?.closed_at
        || '';
    },
    displayedCashCountLines() {
      return Array.isArray(this.cashCount?.lines)
        ? this.cashCount.lines.filter((line) => Number(line.quantity || 0) > 0)
        : [];
    },
    todayDate() {
      return formatLocalDate(this.currentClock instanceof Date ? this.currentClock : new Date());
    },
    isSelectedDateInFuture() {
      return Boolean(this.selectedDate && this.selectedDate > this.todayDate);
    },
    soldProductColumns() {
      return [
        { key: 'name', label: 'Produit', sortable: true },
        { key: 'quantity', label: 'Qté', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'unit_price', label: 'Prix unitaire', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'total_amount', label: 'Total vente', sortable: true, class: 'text-end', headerClass: 'text-end' },
      ];
    },
    walletTransactionColumns() {
      return [
        { key: 'created_at', label: 'Date', sortable: true }, { key: 'reference', label: 'Référence', sortable: true },
        { key: 'customer_phone', label: 'Numéro client' }, { key: 'operator_reference', label: 'Réf. opérateur' },
        { key: 'wallet.name', label: 'Portefeuille', sortable: true }, { key: 'type', label: 'Opération', sortable: true },
        { key: 'amount', label: 'Montant', sortable: true, class: 'text-end' },
        { key: 'balance_after', label: 'Solde après', sortable: true, class: 'text-end' },
        { key: 'status', label: 'Statut', sortable: true },
      ];
    },
    walletTransactions() { return Array.isArray(this.summary.wallet_transactions) ? this.summary.wallet_transactions : []; },
    stockAlertColumns() {
      return [
        { key: 'name', label: 'Produit', sortable: true },
        { key: 'sku', label: 'SKU', sortable: true },
        { key: 'stock_quantity', label: 'Stock', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'min_stock_quantity', label: 'Seuil', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'alert_type', label: 'État', sortable: true },
      ];
    },
    soldProductRows() {
      return Array.isArray(this.summary.sold_products)
        ? this.summary.sold_products.map((product, index) => ({
          ...product,
          _row_key: [
            product.product_id ?? 'none',
            product.name || 'product',
            product.unit_price ?? 0,
            index,
          ].join('-'),
        }))
        : [];
    },
    soldProducts() {
      return this.soldProductRows.filter((product) => this.matchesSearch(product, this.soldProductsSearch, [
        'name',
        'quantity',
        'unit_price',
        'total_amount',
      ]));
    },
    stockAlertRows() {
      return Array.isArray(this.summary.stock_alert_products)
        ? this.summary.stock_alert_products.map((product, index) => ({
          ...product,
          _row_key: product.id || product.sku || `${product.name || 'product'}-${index}`,
        }))
        : [];
    },
    stockAlertProducts() {
      return this.stockAlertRows.filter((product) => this.matchesSearch({
        ...product,
        alert_label: this.stockAlertLabel(product),
      }, this.stockAlertProductsSearch, [
        'name',
        'sku',
        'stock_quantity',
        'min_stock_quantity',
        'alert_label',
      ]));
    },
    stockOutProductsCount() {
      const fallback = this.stockAlertRows
        .filter((product) => this.stockAlertType(product) === 'out_of_stock')
        .length;

      return Number(this.summary.stock_out_products_count ?? fallback);
    },
    stockMinimumProductsCount() {
      const fallback = this.stockAlertRows
        .filter((product) => this.stockAlertType(product) === 'minimum_reached')
        .length;

      return Number(this.summary.stock_minimum_products_count ?? fallback);
    },
    pendingSalesCount() {
      return Number(this.summary.pending_sales_count || 0);
    },
    pendingSalesBlockingMessage() {
      if (this.pendingSalesCount === 1) {
        return 'Vous avez 1 vente en attente. Veuillez d’abord la finaliser ou la supprimer avant de clôturer la journée.';
      }

      return `Vous avez ${this.pendingSalesCount} ventes en attente. Veuillez d’abord les finaliser ou les supprimer avant de clôturer la journée.`;
    },
    closeDayButtonTitle() {
      if (this.isSelectedDateInFuture) return 'Impossible de clôturer une journée future';
      if (this.pendingSalesCount > 0) return this.pendingSalesBlockingMessage;

      return 'Clôturer ma journée';
    },
    hasSalesForDay() {
      return this.soldProductRows.length > 0;
    },
    isWalletEnabled() {
      return this.$store?.getters?.['runtime/moduleEnabled']?.('wallet') !== false;
    },
    cashTotalColumnClass() {
      return this.isWalletEnabled ? 'col-xl-2' : 'col-xl-4';
    },
    hasWalletTransactions() {
      return this.isWalletEnabled && this.walletTransactions.length > 0;
    },
    hasSessionOrCashCount() {
      return Boolean(this.summary.session || this.cashCount);
    },
  },
  async mounted() {
    this.startClock();
    await this.initializeEndOfDay();
  },
  beforeUnmount() {
    this.stopClock();
  },
  methods: {
    walletOperationLabel(type) { return ({ recharge: 'Recharge', deposit: 'Dépôt', withdrawal: 'Retrait', adjustment: 'Ajustement', cancellation: 'Annulation' })[type] || type; },
    walletStatusLabel(status) {
      if (status === 'validated') return 'Validée';
      if (status === 'cancelled') return 'Annulée';

      const label = String(status || 'Inconnu').replace(/[_-]+/g, ' ');
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    walletStatusBadgeClass(status) {
      if (status === 'validated') return 'bg-success';
      if (status === 'cancelled') return 'bg-danger';

      return 'bg-secondary';
    },
    async initializeEndOfDay() {
      const shopRefresh = this.$store.dispatch('shop/fetchInfo', { silent: true });

      try {
        const response = await this.$api.get('/cash-register-sessions/current');
        const data = response.data?.data || response.data || {};
        const referenceSession = data.has_open_session && data.session
          ? data.session
          : data.latest_session;
        const operationDate = this.datePartFromValue(referenceSession?.operation_date)
          || this.datePartFromValue(referenceSession?.opened_at);

        if (operationDate) {
          this.selectedDate = operationDate;
        }
      } catch (error) {
        // Le résumé reste accessible même si la recherche de session échoue.
      }

      await Promise.allSettled([shopRefresh]);
      await this.fetchSummary();
    },
    async fetchSummary() {
      this.isLoading = true;
      this.errorMessage = '';
      if (this.isSelectedDateInFuture) {
        this.summary = {};
        this.isLoading = false;
        return;
      }

      try {
        const response = await this.$api.get('/cash-counts/day-summary', {
          params: { date: this.selectedDate },
        });
        this.summary = response.data.data || response.data || {};
      } catch (error) {
        this.errorMessage = 'Impossible de charger la fin de journée.';
      } finally {
        this.isLoading = false;
      }
    },
    async openCreateDialog() {
      if (this.isSelectedDateInFuture) {
        this.drawerWarningMessage = '';
        this.errorMessage = '';
        return;
      }

      if (this.pendingSalesCount > 0) {
        showAppToast({
          type: 'warning',
          message: this.pendingSalesBlockingMessage,
        });
        return;
      }

      this.currentClock = new Date();
      this.openDrawerForDayClosing();
      this.formDialogVisible = true;
    },
    closeDialog() {
      this.formDialogVisible = false;
    },
    async handleCashCountSaved() {
      this.formDialogVisible = false;
      this.successMessage = 'Journée clôturée.';

      if (this.selectedDate === this.todayDate) {
        await this.$store.dispatch('cashRegister/clear');
      }

      await this.fetchSummary();
    },
    handleSoldProductsSortChange({ sortBy, sortDir }) {
      this.soldProductsTable.sortBy = sortBy;
      this.soldProductsTable.sortDir = sortDir;
    },
    handleStockAlertProductsSortChange({ sortBy, sortDir }) {
      this.stockAlertProductsTable.sortBy = sortBy;
      this.stockAlertProductsTable.sortDir = sortDir;
    },
    matchesSearch(row, search, fields) {
      const keyword = String(search || '').trim().toLowerCase();
      if (!keyword) return true;

      return fields.some((field) => String(row?.[field] ?? '').toLowerCase().includes(keyword));
    },
    async openDrawerForDayClosing() {
      this.drawerWarningMessage = '';

      if (!this.cashDrawerEnabled) return;

      try {
        await openThermalDrawer(loadThermalPrinterSettings());
      } catch (error) {
        const message = error instanceof Error
          ? error.message
          : 'Impossible d’ouvrir le tiroir-caisse automatiquement.';

        this.drawerWarningMessage = `Billetage possible, mais le tiroir-caisse ne s’est pas ouvert automatiquement. ${message}`;
      }
    },
    datePartFromValue(value) {
      if (!value) return '';
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }

      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '';

      if (this.shopTimezone) {
        try {
          const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: this.shopTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).formatToParts(date);
          const dateParts = Object.fromEntries(parts.map((part) => [part.type, part.value]));

          return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
        } catch (error) {
          return '';
        }
      }

      const pad = (number) => String(number).padStart(2, '0');

      return [
        date.getFullYear(),
        pad(date.getMonth() + 1),
        pad(date.getDate()),
      ].join('-');
    },
    formatTimeWithSeconds(value, useSiteTimezone = false) {
      if (!value) return '-';

      if (!useSiteTimezone && typeof value === 'string') {
        const timeMatch = value.match(/T(\d{2}:\d{2}:\d{2})/);
        if (timeMatch) return timeMatch[1];
      }

      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '-';

      if (useSiteTimezone && this.shopTimezone) {
        try {
          return new Intl.DateTimeFormat('fr-FR', {
            timeZone: this.shopTimezone,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
          }).format(date);
        } catch (error) {
          return '-';
        }
      }

      const pad = (number) => String(number).padStart(2, '0');

      return [
        pad(date.getHours()),
        pad(date.getMinutes()),
        pad(date.getSeconds()),
      ].join(':');
    },
    stockAlertType(product) {
      if (product?.alert_type) return product.alert_type;

      return Number(product?.stock_quantity || 0) <= 0 ? 'out_of_stock' : 'minimum_reached';
    },
    stockAlertLabel(product) {
      return this.stockAlertType(product) === 'out_of_stock' ? 'Rupture' : 'Seuil minimum';
    },
    stockAlertBadgeClass(product) {
      return this.stockAlertType(product) === 'out_of_stock'
        ? 'bg-danger'
        : 'bg-warning text-dark';
    },
    startClock() {
      this.currentClock = new Date();
      this.clockIntervalId = window.setInterval(() => {
        this.currentClock = new Date();
      }, 30000);
    },
    stopClock() {
      if (this.clockIntervalId) {
        window.clearInterval(this.clockIntervalId);
        this.clockIntervalId = null;
      }
    },
  },
};
</script>

<style scoped>
.end-day-date {
  width: 160px;
}

.end-day-closing-card {
  background: linear-gradient(135deg, #f8fbff 0%, #eef7ff 100%);
  border: 1px solid rgba(14, 165, 233, 0.16) !important;
}

.end-day-real-closing-card {
  background: linear-gradient(135deg, #f9fffb 0%, #eefbf3 100%);
  border: 1px solid rgba(34, 197, 94, 0.18) !important;
}

.closing-time {
  align-items: center;
  color: #0369a1;
  display: inline-flex;
  font-size: 1.15rem;
  font-weight: 800;
}

.real-closing-time {
  align-items: center;
  color: #15803d;
  display: inline-flex;
  font-size: 1.15rem;
  font-weight: 800;
}

.btn-close-day-heartbeat {
  animation: close-day-heartbeat 2s ease-in-out infinite;
  border-radius: 0.8rem;
  box-shadow: 0 14px 28px rgba(37, 99, 235, 0.28);
  font-weight: 800;
  min-height: 52px;
  padding: 0.85rem 1.35rem;
}

.stock-alert-counters .badge {
  font-size: 0.85rem;
  padding: 0.45rem 0.65rem;
}

@keyframes close-day-heartbeat {
  0%,
  28%,
  70%,
  100% {
    transform: scale(1);
  }

  14%,
  42% {
    transform: scale(1.045);
  }
}
</style>
